import { identityKeys, signOutOfScout } from '@scout/identity'
import { leaveBoundary, signInOffered } from '@scout/identity/hanko'
import { SidebarMenuItem } from '@scout/ui/sidebar'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useCallback, useState } from 'react'

/**
 * The way out.
 *
 * Scout has no sign-out endpoint and does not want one. The boundary's session
 * is the only session there is — nothing is held server-side to end — so an
 * endpoint here would answer 200 having changed nothing, which is the kind of
 * lie later code comes to rely on. Ending it at the boundary is the whole of
 * the server's half.
 *
 * Scout's half is forgetting. Everything cached was read on a buyer's behalf,
 * and a browser that has signed out must be indistinguishable from one that
 * never signed in — so the cache is cleared outright rather than invalidated.
 * Invalidating would leave the data in place and refetch it, which on a
 * signed-out browser means rendering a buyer's briefs from cache while the 401s
 * arrive behind them.
 *
 * The order is load-bearing: the boundary first, then the cache. Clearing first
 * would let a refetch that is already in flight — still authenticated, because
 * the session has not ended yet — repopulate what was just dropped.
 */
export function SignOut() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [leaving, setLeaving] = useState(false)

  const signOut = useCallback(() => {
    setLeaving(true)

    void (async () => {
      try {
        await leaveBoundary()
      } catch {
        // Forget anyway. A boundary that cannot be reached is a reason to be
        // careful, not a reason to leave a buyer's briefs on the screen of
        // someone who has asked to leave.
      }

      try {
        // The half that actually stops Scout recognising this browser. Without
        // it the cookie survives, /me keeps answering with the person, and the
        // sidebar goes on showing a buyer who has asked to leave.
        await signOutOfScout()
      } catch {
        // Same reasoning: forget locally even if this failed. What is left is a
        // browser that may still be recognised, which a reload resolves — not a
        // browser stuck mid-exit.
      }

      // And say plainly that this browser is nobody.
      //
      // Clearing alone is not enough, which is not obvious and cost a second
      // attempt at this bug: an observer that is mounted when the cache is
      // cleared under it goes on reporting the data it last saw. The refetch
      // happens and honestly answers 401, and the sidebar still shows the
      // person, because the components never re-read. Writing the answer in is
      // what actually moves them — every `useMe` is notified, settles on
      // nobody, and the sidebar becomes a stranger's.
      //
      // Said before anything is dropped, and said rather than refetched.
      queryClient.setQueryData(identityKeys.me, null)

      // Then forget everything read for the buyer. resetQueries rather than
      // clear: clear() removes the queries outright and detaches the observers
      // watching them, so a mounted component goes on reporting the last thing
      // it saw and never hears what replaced it — which is why signing out left
      // the sidebar showing a buyer who had gone. Resetting keeps what is
      // watching attached, which is the whole point.
      void queryClient.resetQueries()

      // Released before leaving, so nothing can strand the control in its
      // loading state. This component lives in the sidebar and is not unmounted
      // by navigating, so a flag left set here stays set for good — which is
      // exactly how signing out came to spin forever.
      setLeaving(false)

      void navigate({ to: '/' })
    })()
  }, [navigate, queryClient])

  // Nobody can be signed in, so there is nobody to sign out.
  if (!signInOffered()) return null

  return (
    <SidebarMenuItem value="sign-out" onActivate={signOut} disabled={leaving}>
      Log out
    </SidebarMenuItem>
  )
}
